import type { HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef } from 'react';

import classNames from 'classnames';

import styles from './Box.module.css';

import type {
  BorderRadiusResponsive,
  BorderRadiusSize,
  ColorName,
  ColorNameResponsive,
  SpacingSize,
  SpacingSizeResponsive,
  SpacingValue,
} from '../../styles';
import {
  getBorderRadiusStyles,
  getColorStyles,
  getSpacingStyles,
} from '../../styles';
import { type AlignProps, getAlignStyles } from '../../styles/align';

/** The props for the {@link Box} component. */
export interface BoxProps
  extends AlignProps, PropsWithChildren<HTMLAttributes<HTMLElement>> {
  /** The HTML element to render. */
  as?:
    | 'div'
    | 'section'
    | 'header'
    | 'footer'
    | 'article'
    | 'aside'
    | 'nav'
    | 'main'
    | 'blockquote'
    | 'figure';
  className?: string;
  /** Set the background color. */
  backgroundColor?: ColorName | ColorNameResponsive;
  /** Set the border color. */
  borderColor?: ColorName | ColorNameResponsive;
  /** Set the border radius. */
  borderRadius?: BorderRadiusSize | BorderRadiusResponsive;
  /** Set the margins. */
  m?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the horizontal margins. */
  mx?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the vertical margins. */
  my?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the bottom margin. */
  mb?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the left margin. */
  ml?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the right margin. */
  mr?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the top margin. */
  mt?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the padding. */
  p?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the horizontal padding. */
  px?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the vertical padding. */
  py?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the bottom padding. */
  pb?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the left padding. */
  pl?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the right padding. */
  pr?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the top padding. */
  pt?: SpacingValue | SpacingSize | SpacingSizeResponsive;
}

/**
 * Use `Box` as a basic building block container that can be programmatically
 * styled to fit your needs.
 *
 * @example
 *
 * ```
 * <Box border="Oyster2" p={{ md: 'sm', lg: 'md' }}>
 *   <Text variant="heading-28">Title</Text>
 *   <Text variant="heading-16">Some content</Text>
 * </Box>;
 * ```
 *
 * @see https://corgi-x.tfd.engineering/components/box
 */
export const Box = forwardRef<HTMLElement, BoxProps>((props, ref) => {
  const {
    as: Component = 'div',
    children,
    className,

    align,
    backgroundColor,

    // border props
    borderColor,
    borderRadius,

    // margin props
    m,
    mx,
    my,
    mb,
    ml,
    mr,
    mt,

    // padding props
    p,
    px,
    py,
    pb,
    pl,
    pr,
    pt,

    ...restProps
  } = props;
  const alignStyles = getAlignStyles(props);
  const backgroundColorStyles = getColorStyles('background', backgroundColor);
  const borderColorStyles = getColorStyles('border', borderColor);
  const borderRadiusStyles = getBorderRadiusStyles(borderRadius);

  const marginStyles = getSpacingStyles('margin', {
    spacing: m,
    hSpacing: mx,
    vSpacing: my,
    bottomSpacing: mb,
    leftSpacing: ml,
    rightSpacing: mr,
    topSpacing: mt,
  });

  const paddingStyles = getSpacingStyles('padding', {
    spacing: p,
    hSpacing: px,
    vSpacing: py,
    bottomSpacing: pb,
    leftSpacing: pl,
    rightSpacing: pr,
    topSpacing: pt,
  });

  const cssClasses = classNames(
    { [styles.border]: Boolean(borderColor) },
    alignStyles,
    backgroundColorStyles,
    borderColorStyles,
    borderRadiusStyles,
    marginStyles,
    paddingStyles,
    className
  );

  return (
    <Component
      {...restProps}
      /* @ts-expect-error: these refs do not work with `as` prop. This should be fixed by React 19 */
      ref={ref}
      className={cssClasses || undefined}
    >
      {children}
    </Component>
  );
});
